import { oc } from "@orpc/contract";
import { z } from "zod";

/** A frequently asked question as the admin edits it. `answer` is markdown; the shop renders it. */
export const FaqItem = z.object({
	id: z.string(),
	question: z.string(),
	answer: z.string(),
	published: z.boolean(),
	/** Goes up by one on every save; an update sends the version it was loaded at. */
	version: z.number().int(),
	createdAt: z.iso.datetime(),
	updatedAt: z.iso.datetime(),
});
export type FaqItem = z.infer<typeof FaqItem>;

/** What the admin's form sends to create a question. A new question goes to the end of the list. */
export const FaqItemFields = z.object({
	question: z.string().trim().min(1).max(500),
	answer: z.string().trim().min(1).max(20_000),
	published: z.boolean(),
});

/** What the form sends to save a question: every field, plus the `version` it loaded. */
export const FaqItemUpdate = FaqItemFields.extend({ version: z.number().int().positive() });

/** The new order: the id of every question, each once, first to last. */
export const FaqOrder = z.object({
	ids: z
		.array(z.string())
		.max(1000)
		.refine((ids) => new Set(ids).size === ids.length, "each question once"),
});

/** A published question as the shop shows it. */
export const PublishedFaqItem = z.object({
	id: z.string(),
	question: z.string(),
	answer: z.string(),
});
export type PublishedFaqItem = z.infer<typeof PublishedFaqItem>;

const ById = z.object({ id: z.string() });

/**
 * Frequently asked questions, in the order Richard sets. `/admin/...` needs a staff session and
 * lists every question in order; `/shop/faq-items` is public and lists the published ones in order.
 */
export const FaqItemContract = {
	list: oc.route({ method: "GET", path: "/admin/faq-items" }).output(z.array(FaqItem)),
	create: oc
		.route({ method: "POST", path: "/admin/faq-items", successStatus: 201 })
		.input(FaqItemFields)
		.output(FaqItem),
	get: oc.route({ method: "GET", path: "/admin/faq-items/{id}" }).input(ById).output(FaqItem),
	update: oc
		.route({ method: "PUT", path: "/admin/faq-items/{id}" })
		.input(FaqItemUpdate.extend(ById.shape))
		.output(FaqItem),
	delete: oc.route({ method: "DELETE", path: "/admin/faq-items/{id}", successStatus: 204 }).input(ById),
	/** Sets the order of every question at once; answers the list in its new order. */
	reorder: oc
		.route({ method: "PUT", path: "/admin/faq-items/order" })
		.input(FaqOrder)
		.output(z.array(FaqItem)),
	listPublished: oc.route({ method: "GET", path: "/shop/faq-items" }).output(z.array(PublishedFaqItem)),
};
